import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from './useApi';
import { useModeAwareQuery } from './useModeAwareQuery';
import type { CustomerAddressesApiResponse, StubResponse } from '@/types/api/customer-detail';
import { isStubResponse } from '@/types/api/customer-detail';
import { mapCustomerAddressApiToUi } from '@/lib/mappers/customer-detail';
import type { CustomerAddressUI } from '@/types/ui/customer-detail';

interface AddressInput {
  contactName?: string;
  email?: string;
  phone?: string;
  fax?: string;
  addressLine?: string;
  town?: string;
  county?: string;
  postCode?: string;
  country?: string;
  countryCode?: string;
  isInvoice: boolean;
  isStatement: boolean;
  isDelivery: boolean;
  isPrimary: boolean;
  contactId?: number;
  notes?: string;
}

interface AddressUpdateInput {
  contactName?: string;
  email?: string;
  phone?: string;
  fax?: string;
  addressLine?: string;
  town?: string;
  county?: string;
  postCode?: string;
  country?: string;
  countryCode?: string;
  isInvoice?: boolean;
  isStatement?: boolean;
  isDelivery?: boolean;
  isPrimary?: boolean;
  contactId?: number;
  notes?: string;
}

/**
 * Mode-aware query hook for fetching customer addresses.
 * Returns addresses reactively via useModeAwareQuery, respecting the
 * current screen's Demo/Live toggle.
 */
export function useCustomerAddressesList(customerId: string) {
  const { apiGet } = useApi();

  return useModeAwareQuery<CustomerAddressUI[], Error>(
    ["customer-addresses", customerId],
    async ({ signal }) => {
      if (!customerId || customerId === "0") return [];
      const response = await apiGet<CustomerAddressesApiResponse | StubResponse>(
        `/api/v1/customers/${customerId}/addresses`,
        undefined,
        signal
      );

      if (isStubResponse(response)) {
        return [];
      }

      return response.addresses.map(mapCustomerAddressApiToUi);
    },
    [],
    {
      enabled: !!customerId && customerId !== "0",
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

/**
 * Hook for customer address mutation operations (create, update, delete).
 * Uses authenticated API calls with proper headers.
 */
export function useCustomerAddresses(customerId: string) {
  const { apiPost, apiPut, apiDelete } = useApi();
  const queryClient = useQueryClient();

  const invalidateAddresses = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["customer-addresses", customerId] });
    queryClient.invalidateQueries({ queryKey: ["customer-detail", customerId] });
  }, [queryClient, customerId]);

  const createAddress = useCallback(async (address: AddressInput) => {
    const result = await apiPost<unknown>(
      `/api/v1/customers/${customerId}/addresses`,
      address
    );
    invalidateAddresses();
    return result;
  }, [customerId, apiPost, invalidateAddresses]);

  const updateAddress = useCallback(async (addressId: number, address: AddressUpdateInput) => {
    const result = await apiPut<unknown>(
      `/api/v1/customers/${customerId}/addresses/${addressId}`,
      address
    );
    invalidateAddresses();
    return result;
  }, [customerId, apiPut, invalidateAddresses]);

  const deleteAddress = useCallback(async (addressId: number) => {
    const result = await apiDelete<void>(
      `/api/v1/customers/${customerId}/addresses/${addressId}`
    );
    invalidateAddresses();
    return result;
  }, [customerId, apiDelete, invalidateAddresses]);

  const setAddressAsPrimary = useCallback(async (addressId: number) => {
    return updateAddress(addressId, { isPrimary: true });
  }, [updateAddress]);

  return {
    createAddress,
    updateAddress,
    deleteAddress,
    setAddressAsPrimary,
  };
}
